import React, { useState, useEffect } from 'react';
import { useNavigate, createSearchParams, useSearchParams } from 'react-router-dom';
import axios from "axios";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';
import { Card, Stack, Typography } from "@mui/material";
import { BarChart } from '@mui/x-charts/BarChart';
import { COLOR_BAR_CODE } from '../../../constant/index';
import { MARKET_DASHBOARD_COMPONENTS, MARKET_DASHBOARD_DAYS, BASE_URL } from '../../../constant/index';
import { getMdbData, transformMdbData } from "../../../utils/getMdbData";

const ChartGenerate = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const initialComponent = searchParams.get('component') || 'Nifty 50';
	const [ topLabels, setTopLabels ] = useState([]);
	const [ topData, setTopData ] = useState([]);
	const [ component, setSelectedComponents ] = useState(initialComponent);
	const [ day, setSelectedDay ] = useState('1-Day');
	const [ chartCategory, setChartCategory ] = useState('top');

    const barColors = topData.map((val, index) => COLOR_BAR_CODE[index % COLOR_BAR_CODE.length]);

	const getMarketDashboardData = async () => {
		try {
			const response = await axios.get(
				`${BASE_URL}/getMarketDashboardDataInternally?component=${encodeURIComponent(component)}&day=${encodeURIComponent(day)}`
			);

			const res = await response.data;
			if (res?.statusCode === 503) return;

			if (res?.arStockDataMaster && res?.arStockMarketData?.length > 0) {
				const { arStockDataMaster, arStockMarketData, dateObj } = res;
				const transformed = transformMdbData(arStockMarketData, dateObj);

				// reset before setting new data
				setTopLabels([]);
				setTopData([]);

				const { topLabels, topData } = getMdbData(arStockDataMaster, transformed, component, day, chartCategory);
				setTopLabels(topLabels);
				setTopData(topData);
			}
		} catch (err) {
			console.error(err);
		}
	};

	useEffect(() => {
		if (!component || !day) return;
		const timeout = setTimeout(() => {
			getMarketDashboardData();
		}, 200);
		return () => clearTimeout(timeout);
	}, [component, day, chartCategory]);

	useEffect(() => {
		const currentParam = searchParams.get('component');
		if (currentParam !== component) {
			navigate({
				search: `?${createSearchParams({ component })}`,
			});
		}
	}, [component]);

	const onComponentSelection = (value) => {
		setTopLabels([]);
		setTopData([]);
		setSelectedComponents(value);
	}

	const onDaySelection = (value) => {
		setTopLabels([]);
		setTopData([]);
		setSelectedDay(value);
	}

    return (
        <div id="chart-container" className="container" style={{ paddingTop: '35px' }}>
			<div className="row">
				<div className="col-4">
					<div className="md-links" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 0.6fr)', gap: '2px' }}>
						{MARKET_DASHBOARD_COMPONENTS.map(({ key, value, label }) => (
							<button 
								key={key} 
								onClick={() => onComponentSelection(value)} 
								className={`btn mdb-button-width ${value === component ? 'btn-primary button-cus' : 'btn-outline-secondary'}`}
							>
								{label}
							</button>
						))}
					</div>
				</div>
				<div className="col-8">
					<div className="days-links">
						{
							MARKET_DASHBOARD_DAYS.map(({ key, value, label, tooltipContent }) => {
								return (
									<Tooltip key={key} title={tooltipContent || ''}>
										<button 
                                            className={"btn " + (value === day ? 'btn-primary button-cus' : 'btn-outline-secondary')} 
                                            // onClick={() => onDaySelection(value)}
                                        >{label}</button>
									</Tooltip>
								)
							}) 
						}
					</div>
					<div style={{ 'paddingTop': '10px' }}>
						<RadioGroup
							row
							aria-labelledby="row-radio-buttons-group-label"
							name="row-radio-buttons-group"
							// onChange={onCategoryChange}
							value={chartCategory}
						>
							<FormControlLabel value="top" control={<Radio disabled={component === 'Sectoral Indices'} />} label="Top 10" />
							<FormControlLabel value="bottom" control={<Radio disabled={component === 'Sectoral Indices'} />} label="Bottom 10" />
						</RadioGroup>
					</div>

					<div className="chart-container">
                        <Card sx={{ width: 900, height: 500 }}>
                            <div style={{ paddingLeft: 40 }}>
                                <Stack direction="row" justifyContent="center" spacing={3}>
                                    <Typography variant="h6">{`NSE: ${day} % performance of ${component} components.`}</Typography>
                                </Stack>
                                <BarChart
                                    width={750}
                                    height={470}
                                    series={[{ data: topData, barSize: 5 }]}
                                    layout="horizontal"
                                    margin={{ left: 145 }}
                                    barLabel={(item, context) => {
                                        const smallBarPadding = context.bar.width < 40 ? 27 : 0;
                                        const fontColor = context.bar.width < 40 ? 'black' : 'white';
                                        return (
                                            <tspan 
                                                dx={smallBarPadding}
                                                fill={fontColor}
                                                fontWeight="bold"
                                                fontSize="12px"  
                                            >
                                                {item.value}
                                            </tspan>
                                        );
                                    }}
                                    yAxis={[
                                        {
                                            data: topLabels,
                                            scaleType: "band",
                                            barSize: 5,
                                            colorMap: {
                                                type: "ordinal",
                                                values: topLabels,
                                                colors: barColors,
                                            },
                                        },
                                    ]}
                                />
                            </div>
                        </Card>
                    </div>
				</div>
			</div>
		</div> 
    )
};

export default ChartGenerate;
